// Validation + role copy for the client-portal user forms (Provider detail →
// Users tab). Ported from next-backoffice-web `pages/provider/components/
// CreateUserModal.jsx`, with the password rule tightened to what core actually
// enforces (`validate.passwordProviderUser` → validator's `isStrongPassword`),
// so a password that passes here is never bounced by the API.

export const CLIENT_ROLE = {
  SUPER_ADMIN: 'client_super_admin',
  ADMIN: 'client_admin',
  STAFF: 'client_staff',
  SUPERVISOR: 'client_supervisor',
  DEVELOPER: 'client_developer',
} as const;

/** What each client role can / cannot do — shown under the role picker. */
export const CLIENT_ROLE_PERMISSIONS: Record<
  string,
  { can: string[]; cannot: string[] }
> = {
  [CLIENT_ROLE.SUPER_ADMIN]: {
    can: [
      'Manage outlets (create, edit, assign)',
      'Invite & manage all users (Super Admin, Admin, Staff)',
      'Manage system integrations (host-to-host)',
      'View & manage all data across outlets',
    ],
    cannot: [],
  },
  [CLIENT_ROLE.ADMIN]: {
    can: [
      'Invite Admin & Staff',
      'Manage users (except Super Admin)',
      'Create & manage deliveries',
      'Create & manage addresses',
    ],
    cannot: [
      'Manage outlets',
      'Change outlet assignment',
      'Invite Super Admin',
      'Manage system integrations',
    ],
  },
  [CLIENT_ROLE.STAFF]: {
    can: ['Create & manage deliveries', 'Create & manage addresses'],
    cannot: [
      'Invite or manage users',
      'Manage outlets',
      'Change outlet assignment',
      'Manage system integrations',
    ],
  },
  [CLIENT_ROLE.DEVELOPER]: {
    can: ['Access delivery page', 'Access developer page'],
    cannot: [
      'Access & manage address page',
      'Access & manage outlet page',
      'Access & manage billing page',
    ],
  },
};

// validator.js `isStrongPassword` symbol set (core uses its defaults).
const PASSWORD_SYMBOL = /[-#!$@£%^&*()_+|~=`{}[\]:";'<>?,./\\ ]/;

export const PASSWORD_RULE_HINT =
  'Min. 8 characters with an uppercase letter, a lowercase letter, a number and a symbol.';

/** Mirrors core's `passwordProviderUser` rule. Empty string = valid. */
export const validatePassword = (password: string): string => {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password))
    return 'Password must contain at least 1 uppercase letter';
  if (!/[a-z]/.test(password))
    return 'Password must contain at least 1 lowercase letter';
  if (!/\d/.test(password)) return 'Password must contain at least 1 number';
  if (!PASSWORD_SYMBOL.test(password))
    return 'Password must contain at least 1 symbol (e.g. ! @ # $)';
  return '';
};

export const validateConfirmPassword = (
  password: string,
  confirmPassword: string
): string => {
  if (!confirmPassword) return 'Please confirm the password';
  if (password !== confirmPassword) return 'Passwords do not match';
  return '';
};

export const validateEmail = (email: string): string => {
  const value = email.trim();
  if (!value) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email';
  return '';
};

/**
 * Local part of an Indonesian mobile number, typed after a fixed `+62`
 * prefix: must start with 8 and be 9–13 digits. Optional — empty is valid.
 */
export const validatePhone = (phone: string): string => {
  if (!phone) return '';
  if (!phone.startsWith('8')) return 'Phone number must start with 8';
  if (phone.length < 9) return 'Phone number must be at least 9 digits';
  if (phone.length > 13) return 'Phone number must not exceed 13 digits';
  return '';
};

/** `8123…` → `628123…`, the shape core stores. */
export const toStoredPhone = (phone: string): string | null =>
  phone ? `62${phone}` : null;

/** Stored `628123…` / `08123…` / `+628123…` → the local `8123…` part. */
export const toLocalPhone = (stored?: string | null): string => {
  const digits = (stored ?? '').replace(/\D/g, '');
  if (digits.startsWith('62')) return digits.slice(2);
  if (digits.startsWith('0')) return digits.slice(1);
  return digits;
};
